import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { customerService } from '../services/customer';
import { getActiveRestaurantId } from '../utils/roleAccess';
import { Customer, CreateCustomerRequest } from '../types';
import {
  Users,
  Plus,
  Search,
  Edit3,
  Trash2,
  Phone,
  Mail,
  MapPin,
  User,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { PageSkeleton, EmptyState, ButtonLoader } from '../components/LoadingStates';

export const Customers: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const activeRestaurantId = getActiveRestaurantId();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; customer: Customer | null }>({
    isOpen: false,
    customer: null
  });

  const [filters, setFilters] = useState({
    search: '', // Combined search for name and mobile
    email: ''
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [page, setPage] = useState(0);
  const [formData, setFormData] = useState<CreateCustomerRequest>({
    name: '',
    mobile: '',
    email: '',
    customerAddress: '',
    organizationCode: user?.organizationCode || '',
    restaurantId: activeRestaurantId || undefined,
  });

  // Debounce filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setPage(0); // Reset to first page when filters change
    }, 1500);

    return () => clearTimeout(timer);
  }, [filters]);

  // Fetch customers using search API when there's a search term, otherwise use list API
  const { data: customersData, isLoading } = useQuery(
    ['customers', user?.organizationCode, activeRestaurantId, debouncedFilters, page],
    async () => {
      if (debouncedFilters.search) {
        // Use search API for name/mobile search
        const searchResults = await customerService.search(user?.organizationCode!, debouncedFilters.search, 100);
        // Filter by email if provided
        const filteredResults = debouncedFilters.email
          ? searchResults.filter(customer =>
              customer.email?.toLowerCase().includes(debouncedFilters.email.toLowerCase())
            )
          : searchResults;

        // Manual pagination for search results
        const startIndex = page * 20;
        const endIndex = startIndex + 20;
        const paginatedResults = filteredResults.slice(startIndex, endIndex);

        return {
          content: paginatedResults,
          totalElements: filteredResults.length,
          totalPages: Math.ceil(filteredResults.length / 20),
          size: 20,
          number: page
        };
      } else {
        // Use list API for email-only filter or no filters
        return customerService.list({
          org: user?.organizationCode!,
          restaurantId: activeRestaurantId,
          email: debouncedFilters.email || undefined,
          page,
          size: 20
        });
      }
    },
    {
      enabled: !!user?.organizationCode,
      keepPreviousData: true
    }
  );

  // Create mutation
  const createMutation = useMutation(customerService.create, {
    onSuccess: () => {
      toast.success('Customer created successfully');
      setIsCreateModalOpen(false);
      resetForm();
      queryClient.invalidateQueries('customers');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create customer');
    }
  });

  // Update mutation
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<Customer> }) => customerService.update(id, data),
    {
      onSuccess: () => {
        toast.success('Customer updated successfully');
        setEditingCustomer(null);
        resetForm();
        queryClient.invalidateQueries('customers');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update customer');
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(customerService.delete, {
    onSuccess: () => {
      toast.success('Customer deleted successfully');
      setDeleteConfirm({ isOpen: false, customer: null });
      queryClient.invalidateQueries('customers');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete customer');
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      mobile: '',
      email: '',
      customerAddress: '',
      organizationCode: user?.organizationCode || '',
      restaurantId: activeRestaurantId || undefined,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCustomer && !activeRestaurantId) {
      toast.error('Please select a restaurant before adding a customer.');
      return;
    }
    if (!formData.name && !formData.mobile && !formData.email) {
      toast.error('At least one of name, mobile, or email is required');
      return;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (editingCustomer) {
      updateMutation.mutate({ id: editingCustomer.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name || '',
      mobile: customer.mobile || '',
      email: customer.email || '',
      customerAddress: customer.customerAddress || '',
      organizationCode: customer.organizationCode
    });
    setIsCreateModalOpen(true);
  };

  const handleDelete = (customer: Customer) => {
    setDeleteConfirm({ isOpen: true, customer });
  };

  const confirmDelete = () => {
    if (deleteConfirm.customer) {
      deleteMutation.mutate(deleteConfirm.customer.id);
    }
  };

  const closeModal = () => {
    setIsCreateModalOpen(false);
    setEditingCustomer(null);
    resetForm();
  };

  const clearFilters = () => {
    setFilters({ search: '', email: '' });
    setDebouncedFilters({ search: '', email: '' });
    setPage(0);
  };

  if (isLoading) {
    return <PageSkeleton type="list" />;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3.5 mb-4 sm:mb-0">
          <div className="grid place-items-center h-12 w-12 rounded-2xl text-white shrink-0"
               style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 14px 32px -12px ${currentTheme.colors.primary[500]}` }}>
            <Users className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: currentTheme.colors.text.primary }}>Customers</h1>
            <p className="text-sm mt-0.5" style={{ color: currentTheme.colors.text.secondary }}>Manage your customer database</p>
          </div>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 font-semibold text-white rounded-xl transition-transform hover:scale-[1.03] active:scale-[0.98]"
          style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 12px 26px -12px ${currentTheme.colors.primary[500]}` }}
        >
          <Plus className="h-5 w-5" />
          Add Customer
        </button>
      </div>

      {/* Filters */}
      <div
        className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-4 mb-6"
        style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.primary }}>
              Search by Name or Mobile
              {filters.search && filters.search !== debouncedFilters.search && (
                <span className="ml-2 text-xs text-amber-600">Searching...</span>
              )}
            </label>
            <div className="relative">
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Search by name or mobile number..."
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <Search className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.primary }}>
              Filter by Email
              {filters.email && filters.email !== debouncedFilters.email && (
                <span className="ml-2 text-xs text-amber-600">Filtering...</span>
              )}
            </label>
            <div className="relative">
              <input
                type="email"
                value={filters.email}
                onChange={(e) => setFilters(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Filter by email address..."
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <Search className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Clear Filters Button */}
        {(filters.search || filters.email) && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Customers List */}
      <div
        className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)]"
        style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}
      >
        <div className="p-6 border-b" style={{ borderColor: currentTheme.colors.border.primary }}>
          <h3 className="text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>
            All Customers ({customersData?.totalElements || 0})
          </h3>
        </div>

        {customersData?.content.length === 0 ? (
          <EmptyState
            icon={<Users className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
            title="No Customers Found"
            description="Get started by adding your first customer"
            action={
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center px-4 py-2 text-white rounded-lg btn-primary transition-colors"
                style={{ backgroundColor: currentTheme.colors.primary[600] }}
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Customer
              </button>
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="ui-thead">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Address
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="ui-tbody">
                  {customersData?.content.map((customer) => (
                    <tr key={customer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: currentTheme.colors.primary[100] }}>
                            <User className="h-5 w-5" style={{ color: currentTheme.colors.primary[600] }} />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{customer.name || 'Unknown'}</div>
                            <div className="text-sm text-gray-500">{customer.customerNo}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {customer.mobile && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Phone className="h-4 w-4 mr-2" />
                              {customer.mobile}
                            </div>
                          )}
                          {customer.email && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Mail className="h-4 w-4 mr-2" />
                              {customer.email}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {customer.customerAddress ? (
                          <div className="flex items-start text-sm text-gray-600">
                            <MapPin className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="line-clamp-2">{customer.customerAddress}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">No address</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(customer)}
                            className="text-indigo-600 hover:text-indigo-900 transition-colors"
                            title="Edit Customer"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(customer)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Delete Customer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {customersData && customersData.totalPages > 1 && (
              <div className="px-6 py-3 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Showing {customersData.number * customersData.size + 1} to{' '}
                    {Math.min((customersData.number + 1) * customersData.size, customersData.totalElements)} of{' '}
                    {customersData.totalElements} customers
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 0}
                      className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm">
                      Page {page + 1} of {customersData.totalPages}
                    </span>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page >= customersData.totalPages - 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                  {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.primary }}>
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Customer name"
                    className="ui-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.primary }}>
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => setFormData(prev => ({ ...prev, mobile: e.target.value }))}
                    placeholder="Mobile number"
                    className="ui-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.primary }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Email address"
                    className="ui-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.primary }}>
                    Address
                  </label>
                  <textarea
                    value={formData.customerAddress}
                    onChange={(e) => setFormData(prev => ({ ...prev, customerAddress: e.target.value }))}
                    placeholder="Customer address"
                    rows={3}
                    className="ui-input"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="px-4 py-2 text-white rounded-lg btn-primary transition-colors disabled:opacity-50"
                    style={{ backgroundColor: currentTheme.colors.primary[600] }}
                  >
                    {createMutation.isLoading || updateMutation.isLoading ? (
                      <ButtonLoader text="Saving..." />
                    ) : (
                      editingCustomer ? 'Update' : 'Create'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        title="Delete Customer"
        message={`Are you sure you want to delete customer "${deleteConfirm.customer?.name || deleteConfirm.customer?.mobile || 'Unknown'}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={confirmDelete}
        onClose={() => setDeleteConfirm({ isOpen: false, customer: null })}
        isLoading={deleteMutation.isLoading}
        type="danger"
      />
    </div>
  );
};